"use client";

import React, { useState } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ArrowRight, CircleCheck, MapPin, TrendingUp, Building, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider } from "@/integrations/forms/FormProvider";
const chartData = [{
  year: "2019",
  value: 185000,
  rent: 1250
}, {
  year: "2020",
  value: 198000,
  rent: 1320
}, {
  year: "2021",
  value: 225000,
  rent: 1450
}, {
  year: "2022",
  value: 255000,
  rent: 1600
}, {
  year: "2023",
  value: 270000,
  rent: 1750
}, {
  year: "2024",
  value: 285000,
  rent: 1850
}];
const chartConfig = {
  value: {
    label: "Avg. Property Value",
    color: "var(--chart-1)"
  },
  rent: {
    label: "Avg. Monthly Rent",
    color: "var(--chart-2)"
  }
};
const benefits = [{
  text: "Off-market deal sourcing in high-growth neighborhoods."
}, {
  text: "Comprehensive ROI and cap rate analysis."
}, {
  text: "Turnkey transition to our property management team."
}, {
  text: "Local expertise in Platte, Clay, and surrounding counties."
}];
export default function InvestmentAcquisition() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: dashboardRef,
    isVisible: isDashboardVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="investment-acquisition" ref={sectionRef} className="relative w-full overflow-hidden bg-background py-20 lg:py-32">
      {}
      <div className={`absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/kansas_city_map_overlay_generic.png)] bg-cover bg-center opacity-[0.03] transition-opacity duration-1000 pointer-events-none ${isSectionVisible ? "opacity-[0.04]" : "opacity-0"}`} aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start space-y-8 transition-all duration-700 ease-out ${isTextVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
                <TrendingUp className="mr-2 size-4" />
                Buying in KC
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl font-serif">
                Strategic Investment Acquisition
              </h2>
              <p className="text-lg text-muted-foreground max-w-[600px] leading-relaxed">
                Expand your portfolio with confidence. Invest KC Property Management identifies high-yield residential properties across the Kansas City metro area, specializing in the robust markets of Platte and Clay counties.
              </p>
            </div>

            <ul className="space-y-4">
              {benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start">
                  <CircleCheck className="mr-3 mt-0.5 size-5 shrink-0 text-secondary" />
                  <span className="text-foreground">{benefit.text}</span>
                </li>)}
            </ul>

            <div className="pt-4">
              <Button size="lg" className="h-12 px-8 text-base shadow-lg shadow-primary/20" onClick={() => setIsDialogOpen(true)}>
                Join Exclusive Investor List
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <p className="mt-3 text-sm text-muted-foreground flex items-center">
                <MapPin className="mr-1.5 size-4" />
                Serving Liberty, North Kansas City, Gladstone & more.
              </p>
            </div>
          </div>

          {}
          <div ref={dashboardRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isDashboardVisible ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"}`}>
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-border/50 to-background blur-sm" />
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-muted/30 border border-border/40 shadow-2xl" />

            <Card className="relative overflow-hidden border-border/60 bg-card/95 backdrop-blur-md shadow-xl">
              <CardHeader className="border-b border-border/40 bg-muted/10 pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Building className="size-5 text-primary" />
                      KC Market Analysis
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      Historical performance in target zip codes
                    </CardDescription>
                  </div>
                  <div className="hidden sm:flex items-center space-x-1 rounded-md bg-background p-1 border border-border/50 shadow-sm">
                    <span className="px-2 py-1 text-xs font-medium bg-muted rounded text-foreground">5Y</span>
                    <span className="px-2 py-1 text-xs font-medium text-muted-foreground">1Y</span>
                    <span className="px-2 py-1 text-xs font-medium text-muted-foreground">YTD</span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="rounded-lg border border-border/50 bg-background/50 p-4 shadow-sm">
                    <p className="text-sm font-medium text-muted-foreground">Avg. Cap Rate</p>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-foreground">7.2%</span>
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center">
                        <TrendingUp className="mr-0.5 size-3" /> +0.4%
                      </span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-background/50 p-4 shadow-sm">
                    <p className="text-sm font-medium text-muted-foreground">Occupancy Rate</p>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-foreground">96.5%</span>
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center">
                        <TrendingUp className="mr-0.5 size-3" /> +1.2%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="h-[240px] w-full">
                  <ChartContainer config={chartConfig} className="h-full w-full">
                    <AreaChart data={chartData} margin={{
                    top: 5,
                    right: 0,
                    left: 0,
                    bottom: 0
                  }}>
                      <defs>
                        <linearGradient id="fillValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="fillRent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-rent)" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="var(--color-rent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.4} />
                      <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={10} tick={{
                      fill: 'var(--muted-foreground)',
                      fontSize: 12
                    }} />
                      <YAxis yAxisId="left" tickLine={false} axisLine={false} tickFormatter={value => `$${value / 1000}k`} tick={{
                      fill: 'var(--muted-foreground)',
                      fontSize: 12
                    }} width={50} />
                      <ChartTooltip cursor={{
                      stroke: 'var(--border)',
                      strokeWidth: 1,
                      strokeDasharray: '4 4'
                    }} content={<ChartTooltipContent indicator="dot" />} />
                      <Area yAxisId="left" type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={2} fill="url(#fillValue)" activeDot={{
                      r: 6,
                      strokeWidth: 0
                    }} />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {}
      {isDialogOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
            <Card className="border-border shadow-2xl">
              <CardHeader className="relative">
                <Button variant="ghost" size="icon" className="absolute right-4 top-4 h-8 w-8 rounded-full" onClick={() => setIsDialogOpen(false)}>
                  <X className="size-4" />
                </Button>
                <CardTitle className="text-2xl font-serif">Investor Inquiry</CardTitle>
                <CardDescription>
                  Join our exclusive list to receive off-market opportunities in Kansas City.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="investor-inquiry-form">
                  <form className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-medium leading-none">
                        Name
                      </label>
                      <input id="name" name="name" required placeholder="Enter your name" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium leading-none">
                        Email Address
                      </label>
                      <input id="email" name="email" type="email" required placeholder="Enter your email" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="questions" className="text-sm font-medium leading-none">
                        Your Questions
                      </label>
                      <textarea id="questions" name="questions" placeholder="Type your question here..." className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                    </div>
                    <Button type="submit" className="w-full h-11 text-base font-medium shadow-md">
                      Submit Inquiry
                    </Button>
                  </form>
                </FormProvider>
              </CardContent>
            </Card>
          </div>
          <div className="absolute inset-0 -z-10" onClick={() => setIsDialogOpen(false)} />
        </div>}
    </section>;
}